import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, PristineChangeEvent } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { TimeInterval } from '@features/trip-type-setup/interfaces';
import { TripTypeSetupGridComponent } from '@features/trip-type-setup/trip-type-setup-grid/trip-type-setup-grid.component';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { VehicleType } from '@shared/constants/vehicle-type';
import { combineLatest, filter, map, Observable, take, tap } from 'rxjs';

import { VehicleTripTypeSetupDto } from './interfaces';
import { VehicleTripTypeSetupTabService } from './vehicle-trip-type-setup-tab.service';
import { VehicleDto } from '@pages/vehicles-page/components/vehicles-grid/interfaces';
import { DialogService } from '@core/services/dialog.service';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
import { SetupTabsSharedService } from '@pages/vehicles-page/services/setup-tabs-shared.service';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';

@Component({
  templateUrl: './vehicle-trip-type-setup-tab.component.html',
  styleUrls: ['./vehicle-trip-type-setup-tab.component.scss'],
  providers: [VehicleTripTypeSetupTabService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VehicleTripTypeSetupTabComponent
  extends TabBase
  implements PendingChanges, OnInit
{
  private api = inject(RestApiService);
  private tabService = inject(VehicleTripTypeSetupTabService);
  private dialog = inject(DialogService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private setupTabsSharedService = inject(SetupTabsSharedService);
  private destroyRef = inject(DestroyRef);
  private gridEntityService = inject(SplitterWithGridHelperService);

  setupGridData = signal<TimeInterval[]>([]);
  gridDataChanged = signal(false);
  noRowsMessageKey = signal<string | undefined>(undefined);

  setupElements: DvDynamicFormElement[];
  formGroup: FormGroup<
    FormOf<Pick<VehicleTripTypeSetupDto, 'Holidays' | 'VehicleIntervalSetting'>>
  >;
  selectedVehicleType: VehicleType;
  canEditGrid: boolean;
  canDeleteGrid: boolean;

  readonly vehicleTripTypeSetupEntityName = Entity.VehicleTripSetting;

  isSaveDisabled = computed(
    () =>
      (!this.formDirty() && !this.gridDataChanged()) ||
      (this.intervalEnabled() && this.currentRowsCount() === 0)
  );

  private tripTypeSetupGrid = viewChild(TripTypeSetupGridComponent);

  private hasEditPermission: boolean;
  private hasDeletePermission: boolean;
  private currentTimeSetupGridData: TimeInterval[];

  private currentRowsCount = signal(0);
  private intervalEnabled = signal(false);
  private formDirty = signal(false);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.setupElements = this.tabService.setupElements;
    this.formGroup = DvFormUtils.createFormGroup(this.setupElements, true);
    this.formGroup.events
      .pipe(
        filter(
          (event): event is PristineChangeEvent =>
            event instanceof PristineChangeEvent
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => this.formDirty.set(!event.pristine));
    this.subscribeToSelectedEntityChanges();
    this.subscribeToVehicleIntervalSettingChanges();
  }

  pendingChangesSave = (): Observable<boolean> =>
    this.saveCustomTripTypeSetup();

  hasChanges = (): boolean => !this.isSaveDisabled();

  onGridDataChanged(value: TimeInterval[]): void {
    this.gridDataChanged.set(true);
    this.currentTimeSetupGridData = value;
    this.currentRowsCount.set(value.length);
  }

  onGridReloadClick(): void {
    this.fetchTabData(this.entityId$.value);
  }

  onSave(): void {
    this.saveCustomTripTypeSetup().subscribe();
  }

  private subscribeToSelectedEntityChanges(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(id => !!id)
      )
      .subscribe(id => {
        if (id === NEW_ENTITY_ROUTE_PARAM) {
          return;
        }
        this.fetchTabData(id);
      });
  }

  private fetchTabData(id: string): void {
    this.setupTabsSharedService
      .getTabData(id)
      .pipe(take(1))
      .subscribe(([res, vehicleData]) => {
        this.selectedVehicleType = res.VehicleType;
        this.currentTimeSetupGridData = res.TimeInterval ?? [];
        this.currentRowsCount.set(this.currentTimeSetupGridData.length);
        this.intervalEnabled.set(!!res.VehicleIntervalSetting);
        this.setupGridData.set(res.TimeInterval ?? []);
        this.updateNoRowsMessage(!!res.VehicleIntervalSetting);
        this.handleAccessRestrictions(
          vehicleData,
          !!res.VehicleIntervalSetting
        );
        this.formGroup.markAsPristine();
        this.gridDataChanged.set(false);
        this.formGroup.patchValue(res, { emitEvent: false });
      });
  }

  private subscribeToVehicleIntervalSettingChanges(): void {
    const { VehicleIntervalSetting } = this.formGroup.controls;
    VehicleIntervalSetting?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isChecked => {
        if (isChecked) {
          this.setupGridData.set([]);
          this.currentTimeSetupGridData = [];
          this.currentRowsCount.set(0);
          this.intervalEnabled.set(true);
          this.toggleGridAndFormEditDelete(!!isChecked);
          this.updateNoRowsMessage(true);
          this.tripTypeSetupGrid()?.openAddDialog();
        } else {
          this.openDeleteCustomSetupConfirmation();
        }
      });
  }

  private updateNoRowsMessage(vehicleIntervalSettingValue: boolean): void {
    this.noRowsMessageKey.set(
      vehicleIntervalSettingValue
        ? 'VehicleTripTypeSetup.NoRowsAddContent'
        : undefined
    );
  }

  private toggleGridAndFormEditDelete(
    vehicleIntervalSettingValue: boolean,
    isRecordDeleted = false
  ): void {
    this.canEditGrid =
      vehicleIntervalSettingValue && !isRecordDeleted && this.hasEditPermission;
    this.canDeleteGrid =
      vehicleIntervalSettingValue &&
      !isRecordDeleted &&
      this.hasDeletePermission;

    const holidaysControl = this.formGroup.controls.Holidays;

    if (isRecordDeleted || !this.hasEditPermission) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
      vehicleIntervalSettingValue
        ? holidaysControl.enable({ emitEvent: false })
        : holidaysControl.disable({ emitEvent: false });
    }
  }

  private openDeleteCustomSetupConfirmation(): void {
    this.dialog
      .open({
        title: ' ',
        contentText: 'VehicleTripTypeSetup.CustomSetupDeleteConfirmationText',
        confirmText: 'General.Confirm',
        cancelText: 'General.Cancel',
        hideHeader: true,
      })
      .closed.pipe(take(1))
      .subscribe(({ source }) => {
        if (source === 'confirm') {
          this.resetCustomTripTypeSetupToInitial();
        } else {
          this.formGroup.controls.VehicleIntervalSetting?.setValue(true, {
            emitEvent: false,
          });
        }
      });
  }

  private resetCustomTripTypeSetupToInitial(): void {
    this.api
      .upsert(Entity.VehicleTripSetting, this.entityId$.getValue(), {
        VehicleIntervalSetting: false,
      })
      .pipe(
        tap(() => {
          this.formGroup.markAsPristine();
          this.gridDataChanged.set(false);
          this.fetchTabData(this.entityId$.getValue());
          this.gridEntityService.refreshRowData(this.entityId$.getValue());
        }),
        take(1)
      )
      .subscribe();
  }

  private saveCustomTripTypeSetup(): Observable<boolean> {
    return this.setupTabsSharedService
      .saveVehicleSetup(this.entityId$.getValue(), {
        ...this.formGroup.getRawValue(),
        TimeInterval: this.currentTimeSetupGridData,
      })
      .pipe(
        take(1),
        tap(() => {
          this.gridDataChanged.set(false);
          this.gridEntityService.refreshRowData(this.entityId$.getValue());
          this.formGroup.markAsPristine();
        }),
        map(() => true)
      );
  }

  private handleAccessRestrictions(
    vehicleData: Pick<
      VehicleDto,
      'Username' | 'ShowRecord' | 'VehicleTypeDescription'
    >,
    vehileIntervalSettingValue: boolean
  ): void {
    combineLatest([
      this.permissionsService.hasPermissions([
        RolesAndPermissions.TripSettingsTypeSetupEdit,
      ]),
      this.permissionsService.hasPermissions([
        RolesAndPermissions.TripSettingsTypeSetupDelete,
      ]),
      this.setupTabsSharedService.isCurrentUsersPersonalVehicle(vehicleData),
    ])
      .pipe(take(1))
      .subscribe(
        ([
          hasEditPermission,
          hasDeletePermission,
          isCurrentUsersPersonalVehicle,
        ]) => {
          this.hasEditPermission =
            (hasEditPermission &&
              vehicleData.VehicleTypeDescription !== VehicleType.Personal) ||
            isCurrentUsersPersonalVehicle;

          this.hasDeletePermission =
            (hasDeletePermission &&
              vehicleData.VehicleTypeDescription !== VehicleType.Personal) ||
            isCurrentUsersPersonalVehicle;

          if (!this.hasEditPermission) {
            this.formGroup.disable({ emitEvent: false });
          }

          this.toggleGridAndFormEditDelete(
            vehileIntervalSettingValue,
            !vehicleData.ShowRecord
          );
        }
      );
  }
}
